import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  DialogTitle,
} from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import type { ConsentType, Market } from '../dataProvider/types';
import { WITHDRAWABLE_CONSENT_OPTIONS } from './consentState';

/**
 * Record a consent withdrawal received off-app (D-consent-records, the one narrow admin write).
 * For the real legal case where a member withdraws consent via a non-app channel (phone, email)
 * and compliance must honour and record it. It APPENDS a `revoke` record sourced
 * `compliance_logged` to the immutable consent history; it never edits or deletes a record, so
 * the derived current consent for the chosen type flips to not-granted by the same
 * latest-record-wins rule as any consumer-app revoke.
 *
 * The write is GUARDED on consent:record_withdrawal (the same hasAccess check the chokepoint
 * applies, here for a create-append rather than a status transition, mirroring the guarded
 * ledger-entry appends) and AUDITED for free by the provider wrapper. A reason note is
 * MANDATORY: how the withdrawal was received, for the record.
 */
export const RecordWithdrawalAction = ({
  user,
  now,
  policyVersionFor,
  onRecorded,
}: {
  user: { id: number; name: string; market: Market };
  now: number;
  /** The standing policy version for a type, recorded on the withdrawal for traceability. */
  policyVersionFor: (type: ConsentType) => string;
  onRecorded: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { role } = useAccessContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consentType, setConsentType] = useState<ConsentType | ''>('');
  const [note, setNote] = useState('');

  const canSubmit = consentType !== '' && note.trim().length > 0 && !loading;

  const close = () => {
    setOpen(false);
    setLoading(false);
    setConsentType('');
    setNote('');
  };

  const doRecord = async () => {
    // canSubmit implies consentType is set; TS narrows it to ConsentType past this guard.
    if (!canSubmit) return;
    setLoading(true);
    try {
      if (
        !hasAccess(
          { role, accessibleRegions: [] },
          { capability: 'consent:record_withdrawal' },
        )
      ) {
        throw new Error('Not permitted');
      }
      await dataProvider.create('consents', {
        data: {
          userId: user.id,
          market: user.market,
          consentType,
          action: 'revoke',
          source: 'compliance_logged',
          policyVersion: policyVersionFor(consentType),
          note: note.trim(),
          occurredAt: new Date(now).toISOString(),
        },
      });
      notify('Withdrawal recorded', { type: 'success' });
      close();
      onRecorded();
    } catch {
      notify('Action not permitted or failed', { type: 'error' });
      close();
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="warning"
        onClick={() => setOpen(true)}
        data-testid="consent-withdrawal-open"
      >
        Record a withdrawal
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Record a consent withdrawal for {user.name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Use this only when a member has withdrawn consent off-app, for
            example by phone or email. It appends a revoke to the consent
            history; it never edits the existing record. Choose the consent and
            record how the withdrawal was received.
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id={`withdrawal-type-${user.id}`}>Consent</InputLabel>
            <Select
              labelId={`withdrawal-type-${user.id}`}
              label="Consent"
              value={consentType}
              inputProps={{ 'data-testid': 'consent-withdrawal-type' }}
              onChange={(e) => setConsentType(e.target.value as ConsentType)}
            >
              {WITHDRAWABLE_CONSENT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="How the withdrawal was received"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            helperText="Recorded with the action, for the audit trail"
            inputProps={{ 'data-testid': 'consent-withdrawal-note' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!canSubmit}
            onClick={() => void doRecord()}
            data-testid="consent-withdrawal-confirm"
          >
            Record withdrawal
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
